import { Parser } from 'marked';
import { MarkedOptions, MarkedRenderer } from 'ngx-markdown';

export type CadixLinkDecision =
  | { kind: 'internal'; href: string }
  | { kind: 'external'; href: string; hostname: string; trustedCorporate: boolean }
  | { kind: 'rejected' };

const LAB_CORP_HOST = 'labcorp.com';

export function classifyCadixLink(
  rawHref: string,
  baseHref = globalThis.location?.href ?? 'https://localhost/',
): CadixLinkDecision {
  const href = rawHref.trim();
  if (!href || href.startsWith('//')) {
    return { kind: 'rejected' };
  }

  let url: URL;
  let base: URL;
  try {
    base = new URL(baseHref);
    url = new URL(href, base);
  } catch {
    return { kind: 'rejected' };
  }

  if (url.username || url.password) {
    return { kind: 'rejected' };
  }
  if (url.origin === base.origin) {
    return { kind: 'internal', href: `${url.pathname}${url.search}${url.hash}` };
  }
  if (url.protocol !== 'https:') {
    return { kind: 'rejected' };
  }

  const hostname = url.hostname.toLowerCase();
  return {
    kind: 'external',
    href: url.href,
    hostname,
    trustedCorporate: hostname === LAB_CORP_HOST || hostname.endsWith(`.${LAB_CORP_HOST}`),
  };
}

export function cadixMarkedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer();

  renderer.html = () =>
    '<span class="cadix-html-omitted">[HTML omitted]</span>';
  renderer.image = ({ text }) =>
    `<span class="cadix-image-omitted">[Image omitted${text ? `: ${escapeHtml(text)}` : ''}]</span>`;
  renderer.link = ({ href, text, tokens }) => {
    const label = tokens?.length ? Parser.parseInline(tokens) : escapeHtml(text);
    const decision = classifyCadixLink(href);
    if (decision.kind === 'rejected') {
      return `<span class="cadix-link-rejected">${label}<span class="visually-hidden"> (link unavailable)</span></span>`;
    }
    if (decision.kind === 'internal') {
      return `<a href="${escapeAttribute(decision.href)}">${label}</a>`;
    }
    const corporateClass = decision.trustedCorporate ? ' cadix-link-corporate' : '';
    return (
      `<a href="${escapeAttribute(decision.href)}" target="_blank" rel="noopener noreferrer" ` +
      `class="cadix-link-external${corporateClass}">${label}` +
      `<span class="material-symbols-rounded cadix-external-link-icon" aria-hidden="true">open_in_new</span>` +
      `<span class="visually-hidden"> (opens ${escapeHtml(decision.hostname)} in a new tab)</span></a>`
    );
  };

  return {
    renderer,
    gfm: true,
    breaks: false,
    pedantic: false,
  };
}

function escapeAttribute(value: string): string {
  return escapeHtml(value).replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
